import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "./ui/input";
import { MapPin, Loader2, Search, X } from "lucide-react";

export default function LocationAutocomplete({ 
  value, 
  onChange, 
  onLocationSelect,
  placeholder = "Start typing an address...",
  className = ""
}) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync external value changes
  useEffect(() => {
    if (value !== query) {
      setQuery(value || "");
    }
  }, [value]);

  const searchLocations = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      // Use OpenStreetMap Nominatim API (free, no key required)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'GBRMS-BCM-Application'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const formattedSuggestions = data.map(item => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          address: item.address,
          type: item.type,
          formatted: formatAddress(item)
        }));
        setSuggestions(formattedSuggestions);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Location search error:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const formatAddress = (item) => {
    const addr = item.address || {};
    const parts = [];
    
    if (addr.house_number && addr.road) {
      parts.push(`${addr.house_number} ${addr.road}`);
    } else if (addr.road) {
      parts.push(addr.road);
    }
    
    if (addr.city || addr.town || addr.village) {
      parts.push(addr.city || addr.town || addr.village);
    }
    
    if (addr.state) {
      parts.push(addr.state);
    }
    
    if (addr.postcode) {
      parts.push(addr.postcode);
    }
    
    if (addr.country) {
      parts.push(addr.country);
    }
    
    return parts.length > 0 ? parts.join(", ") : item.display_name;
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange?.(newValue);
    
    // Debounce the search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      searchLocations(newValue);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.formatted);
    onChange?.(suggestion.formatted);
    onLocationSelect?.({
      address: suggestion.formatted,
      displayName: suggestion.display_name,
      latitude: suggestion.lat,
      longitude: suggestion.lon,
      details: suggestion.address
    });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const clearInput = () => {
    setQuery("");
    onChange?.("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pl-10 pr-10 h-10"
          data-testid="location-autocomplete-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-sky-700" />}
          {query && !loading && (
            <button 
              type="button"
              onClick={clearInput}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" style={{ zIndex: 9999 }}>
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              className="w-full px-4 py-3 text-left hover:bg-sky-50 flex items-start gap-3 border-b border-slate-100 last:border-0 transition-colors"
              onClick={() => handleSuggestionClick(suggestion)}
              data-testid={`location-suggestion-${index}`}
            >
              <MapPin className="w-4 h-4 text-sky-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {suggestion.formatted}
                </p>
                <p className="text-xs text-slate-500 truncate mt-0.5">
                  {suggestion.display_name}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* No results message */}
      {showSuggestions && query.length >= 3 && suggestions.length === 0 && !loading && (
        <div className="absolute w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-4 text-center" style={{ zIndex: 9999 }}>
          <Search className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600">No locations found</p>
          <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
        </div>
      )}
    </div>
  );
}
